<script setup lang="ts">
import { type Ref, ref} from "vue";
import {Money} from "../../../utils/money.ts";
import {label} from '../../mixins/translations.js';
import {useCartStore} from "../../stores/cart";

interface Props {
    showDiscountInput?: boolean
}

const props = withDefaults<Props, Props>(defineProps<Props>(), {
    showDiscountInput: true
})

const cartStore = useCartStore();
</script>

<template>
    <aside class="lg:block">
        <div class="lg:sticky lg:top-40">
            <div class="rounded-md bg-white p-5 shadow-[0_12px_35px_rgba(15,23,42,0.08)] sm:p-6">
                <div class="space-y-5">
                    <div
                        v-for="cartItem in cartStore.items"
                        :key="cartItem.model.id"
                        class="flex gap-4 border-b border-slate-200 pb-5 last:border-b-0 last:pb-0"
                    >
                        <a :href="cartItem.model.item.product.url">
                            <span class="sr-only">
                                {{ cartItem.model.item.product.name }}
                            </span>
                            <img class="h-20 w-20 rounded-md object-cover" :src="cartItem.model.item.product.thumbnail?.image_url" loading="lazy" :alt="cartItem.model.item.name">
                        </a>
                        <div class="min-w-0 flex-1">
                            <div class="flex gap-3">
                                <h3 class="flex-1 text-sm font-semibold leading-normal text-slate-800">{{ cartItem.model.item.product.name }}</h3>
                                <div class="whitespace-nowrap text-sm font-semibold text-slate-800">{{ Money.format(cartItem.price_incl, cartStore.cart.currency) }}</div>
                            </div>
                            <p class="mt-2 text-sm text-slate-500">{{ label('site.product.quantity') }}: {{ cartItem.model.quantity }}</p>
                        </div>
                    </div>
                </div>

                <div class="mt-6 space-y-3 border-t border-slate-200 pt-6 text-sm" v-if="!cartStore.isLoading">
                    <div class="flex items-center justify-between gap-4 text-slate-600">
                        <span>{{ label('site.cart.subtotal_incl') }}</span>
                        <span>{{ Money.format(cartStore.cart.sub_total_incl, cartStore.cart.currency) }}</span>
                    </div>
                    <div class="flex items-center justify-between gap-4 text-slate-600">
                        <span>{{ label('site.cart.subtotal_excl') }}</span>
                        <span>{{ Money.format(cartStore.cart.sub_total_excl, cartStore.cart.currency) }}</span>
                    </div>
                    <div class="flex items-center justify-between gap-4 text-slate-600">
                        <span>{{ label('site.cart.delivery_costs') }}</span>
                        <span>{{ Money.format(cartStore.cart.delivery_cost_incl ?? 0, cartStore.cart.currency) }}</span>
                    </div>
                </div>

                <div class="mt-6 flex items-center justify-between gap-4 border-t border-slate-200 pt-6" v-if="!cartStore.isLoading">
                    <span class="text-sm font-semibold text-slate-800">{{ label('site.cart.total_incl')}}</span>
                    <span class="text-sm font-bold text-slate-900">{{ Money.format(cartStore.cart.total, cartStore.cart.currency) }}</span>
                </div>
            </div>

            <div class="py-6 px-4 sm:px-6" v-if="$slots.actions">
                <slot name="actions"></slot>
            </div>
        </div>
    </aside>

</template>
